import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';

type GeoJson =
  | { type: 'MultiPolygon'; coordinates: number[][][][] }
  | { type: 'Polygon'; coordinates: number[][][] }
  | null;

interface ZonePolygonMapProps {
  initialGeoJson: GeoJson;
  onChange: (geojson: { type: 'MultiPolygon'; coordinates: number[][][][] } | null) => void;
}

/** Metro Manila–ish default view for LGU drawing */
const DEFAULT_CENTER: L.LatLngExpression = [14.55, 121.0];
const DEFAULT_ZOOM = 10;

export function ZonePolygonMap({ initialGeoJson, onChange }: ZonePolygonMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const drawnRef = useRef<L.FeatureGroup | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);

    const drawn = new L.FeatureGroup();
    map.addLayer(drawn);
    drawnRef.current = drawn;

    const drawControl = new L.Control.Draw({
      position: 'topright',
      draw: {
        polygon: {
          allowIntersection: false,
          showArea: true,
          shapeOptions: { color: '#FFD100', fillColor: '#FFD100', fillOpacity: 0.25, weight: 2 },
        },
        polyline: false,
        rectangle: false,
        circle: false,
        circlemarker: false,
        marker: false,
      },
      edit: {
        featureGroup: drawn,
        remove: true,
      },
    });
    map.addControl(drawControl);

    const emit = () => {
      const layers = drawn.getLayers();
      if (!layers.length) {
        onChange(null);
        return;
      }
      const coordinates: number[][][][] = [];
      for (const layer of layers) {
        if (layer instanceof L.Polygon) {
          const latlngs = layer.getLatLngs();
          const rings = (Array.isArray(latlngs[0]) ? latlngs : [latlngs]) as L.LatLng[][];
          coordinates.push(
            rings.map((ring) => {
              const pts = ring.map((p) => [p.lng, p.lat] as [number, number]);
              const first = pts[0];
              const last = pts[pts.length - 1];
              if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
                pts.push([first[0], first[1]]);
              }
              return pts;
            }),
          );
        }
      }
      onChange({ type: 'MultiPolygon', coordinates });
    };

    map.on(L.Draw.Event.CREATED, (e: L.LeafletEvent) => {
      const event = e as unknown as { layer: L.Layer };
      // One polygon per zone for clarity — replace existing
      drawn.clearLayers();
      drawn.addLayer(event.layer);
      emit();
    });
    map.on(L.Draw.Event.EDITED, () => emit());
    map.on(L.Draw.Event.DELETED, () => emit());

    if (initialGeoJson) {
      const layer = L.geoJSON(initialGeoJson as GeoJSON.GeoJsonObject, {
        style: { color: '#0E1216', fillColor: '#FFD100', fillOpacity: 0.25, weight: 2 },
      });
      layer.eachLayer((l) => drawn.addLayer(l));
      const bounds = drawn.getBounds();
      if (bounds.isValid()) map.fitBounds(bounds.pad(0.15));
    }

    mapRef.current = map;

    // Fix grey tiles when mounted in a modal/panel
    setTimeout(() => map.invalidateSize(), 50);

    return () => {
      map.remove();
      mapRef.current = null;
      drawnRef.current = null;
    };
    // Mount once per editor open — parent remounts via key
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="zone-map-wrap">
      <div ref={containerRef} className="zone-map" />
      <p className="map-hint">
        Draw a polygon around the LGUs in this zone. One shape per zone. Edit or delete with the
        toolbar. Historical trips keep their stamped rates if you redraw later.
      </p>
      <style>{`
        .zone-map-wrap { margin-bottom: var(--space-md); }
        .zone-map {
          height: 420px;
          width: 100%;
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          z-index: 0;
        }
        .map-hint {
          font-size: 0.75rem;
          color: var(--color-text-muted);
          margin-top: var(--space-sm);
        }
      `}</style>
    </div>
  );
}
